/**
 * @file Алгоритм загрузки одного файла целиком.
 *
 * Проверка → параллельные сегменты → серверная сборка → очистка.
 * Операции передаются через ports, поэтому алгоритм тестируется без React,
 * настоящего сервера и browser workers.
 */
import {
    UploadError,
    asUploadError,
    minimumTraffic,
    splitFile,
} from './model.ts'
import type {
    SegmentRange,
    SegmentStatus,
    UploadPhase,
    UploadRequest,
} from './model.ts'

export interface UploadHandle {
    id: string
    path: string
}

// ports — переданные операции: в браузере настоящие, в тесте обычные функции-имитации.
export interface UploadPorts {
    preflight: (request: UploadRequest, signal: AbortSignal) => Promise<number>
    create: (
        request: UploadRequest,
        range: SegmentRange,
        runId: string,
        signal: AbortSignal,
    ) => Promise<UploadHandle>
    segment: (
        request: UploadRequest,
        range: SegmentRange,
        handle: UploadHandle,
        signal: AbortSignal,
        progress: (bytes: number, status: SegmentStatus) => void,
    ) => Promise<void>
    finalize: (
        request: UploadRequest,
        handles: UploadHandle[],
        signal: AbortSignal,
    ) => Promise<void>
    remove: (request: UploadRequest, handle: UploadHandle) => Promise<void>
}

export interface UploadEvents {
    phase: (phase: UploadPhase) => void
    segment: (index: number, bytes: number, status: SegmentStatus) => void
    cleanupWarning: () => void
}

export async function runUpload(
    request: UploadRequest,
    runId: string,
    ports: UploadPorts,
    signal: AbortSignal,
    events: UploadEvents,
): Promise<void> {
    const controller = new AbortController()
    const combined = AbortSignal.any([signal, controller.signal])

    // handles собирает серверные идентификаторы частей для сборки и последующей очистки.
    const handles: UploadHandle[] = []

    // Сохраняем итог отдельно, чтобы сначала выполнить допустимую очистку.
    let error: unknown = null
    let keepParts = false

    try {
        combined.throwIfAborted()
        const { file } = request
        if (
            !file.name ||
            ['.', '..'].includes(file.name) ||
            /[/\\\0]/.test(file.name)
        )
            throw new UploadError('invalidFile')

        const ranges = splitFile(file.size)

        // Проверка локальных данных не резервирует квоту: отказ возможен и во время передачи.
        const available = await ports.preflight(request, combined)
        combined.throwIfAborted()
        if (minimumTraffic(file.size, request.password !== '') > available)
            throw new UploadError('quota')

        events.phase('uploading')
        let firstFailure: unknown = null

        // Ждём завершения всех сегментов даже при ошибке одного: нельзя очищать
        // временные части, пока другой worker ещё записывает в них данные.
        await Promise.allSettled(
            ranges.map(async (range) => {
                let uploaded = 0

                try {
                    combined.throwIfAborted()
                    const handle = await ports.create(
                        request,
                        range,
                        runId,
                        combined,
                    )

                    // Сохраняем handle до проверки отмены: сервер мог создать
                    // часть одновременно с нажатием «Отменить». Её тоже надо очистить.
                    handles[range.index] = handle
                    combined.throwIfAborted()
                    await ports.segment(
                        request,
                        range,
                        handle,
                        combined,
                        (bytes, status) => {
                            uploaded = bytes
                            events.segment(range.index, bytes, status)
                        },
                    )
                } catch (cause) {
                    if (firstFailure === null) {
                        firstFailure = cause
                        events.segment(
                            range.index,
                            uploaded,
                            signal.aborted ? 'stopped' : 'failed',
                        )
                    }

                    // Первая ошибка отменяет соседние сегменты, но allSettled
                    // всё равно дождётся их выхода перед очисткой.
                    controller.abort(cause)

                    throw cause
                }
            }),
        )

        if (firstFailure !== null) throw firstFailure

        combined.throwIfAborted()

        // Файл считается загруженным после серверной сборки, а не после последнего PATCH.
        events.phase('finalizing')

        try {
            await ports.finalize(request, handles, combined)
        } catch (cause) {
            if (cause instanceof UploadError) throw cause

            throw new UploadError('finalizationUnknown', true)
        }
    } catch (cause) {
        // Не выбрасываем ошибку сразу: сначала потребуется общая очистка ниже.
        // keepParts означает неопределённый результат сборки — сервер ещё
        // может читать части. В таком случае удалять их нельзя.
        error = cause
        keepParts = asUploadError(cause).keepParts
    }

    // Не удаляем части, пока сервер ещё может собирать из них файл.
    if (!keepParts && handles.some(Boolean)) {
        events.phase('cleaning')
        const cleanup = await Promise.allSettled(
            handles
                .filter(Boolean)
                .map((handle) => ports.remove(request, handle)),
        )
        if (cleanup.some((result) => result.status === 'rejected'))
            events.cleanupWarning()
    }

    // Ошибка очистки показана отдельно; основной результат передачи сохраняется.
    if (error !== null) throw error

    signal.throwIfAborted()
}
